import { Injectable, inject } from '@angular/core';
import { FuseNavigationItem } from '@library/components/navigation';
import { EnterpriseFeatureConfigService } from 'app/services/enterprise-feature-config.service';
import { EnterpriseService } from 'app/services/enterprise.service';
import { SecurityProfileService } from 'app/services/securityProfile.service';
import { UserService } from 'app/services/user.service';
import { BehaviorSubject, Observable } from 'rxjs';
import { ROLES } from 'shared-models';
import { canUserTrackTimeInEnterprise } from '../auth/timerecord-role.util';
import { compactNavigation } from './navigation.data';

@Injectable({ providedIn: 'root' })
export class NavigationService {
  private _navigation: BehaviorSubject<{ compact: FuseNavigationItem[] }> = new BehaviorSubject<{
    compact: FuseNavigationItem[];
  }>({
    compact: [],
  });

  private securityProfileService = inject(SecurityProfileService);
  private enterpriseFeatureConfigService = inject(EnterpriseFeatureConfigService);
  private userService = inject(UserService);
  private enterpriseService = inject(EnterpriseService);
  private _cachedRole: ROLES | null = null;
  private _cachedFeatureSignature = '';
  private _cachedNavigation: FuseNavigationItem[] = [];

  /**
   * Constructor
   */
  constructor() {}

  // -----------------------------------------------------------------------------------------------------
  // @ Accessors
  // -----------------------------------------------------------------------------------------------------

  /**
   * Get all navigation data
   */
  get navigation$(): Observable<{ compact: FuseNavigationItem[] }> {
    return this._navigation.asObservable();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Get navigation
   */
  get(): Observable<{ compact: FuseNavigationItem[] }> {
    const userRole = this.securityProfileService.getRole();
    const featureConfig = this.enterpriseFeatureConfigService.getSnapshot();
    const canTrackTime = canUserTrackTimeInEnterprise(
      this.userService.getUser(),
      this.enterpriseService.selectedEnterprise?.id
    );
    const featureSignature = [
      featureConfig.fileManagementEnabled,
      featureConfig.applicationsEnabled,
      featureConfig.projectsEnabled,
      this.enterpriseService.selectedEnterprise?.id || '',
      canTrackTime,
    ].join('|');

    if (this._cachedRole !== userRole || this._cachedFeatureSignature !== featureSignature) {
      this._cachedRole = userRole;
      this._cachedFeatureSignature = featureSignature;

      const filteredByFeature = compactNavigation.filter((item) => {
        if (item.id === 'file-manager-user' && !featureConfig.fileManagementEnabled) {
          return false;
        }

        if (item.id === 'applications' && !featureConfig.applicationsEnabled) {
          return false;
        }

        if (item.id === 'my-assignments' && !featureConfig.projectsEnabled) {
          return false;
        }

        return true;
      });

      const filteredByTrackableAdmin =
        userRole === ROLES.ADMIN && canTrackTime
          ? filteredByFeature.filter((item) => item.id !== 'home-dashboard')
          : filteredByFeature;

      if (userRole === ROLES.SUPERADMIN) {
        this._cachedNavigation = filteredByTrackableAdmin.filter((item) => item.id !== 'home-dashboard');
      } else if (userRole === ROLES.CONFIG_ADMIN) {
        this._cachedNavigation = filteredByTrackableAdmin.filter(
          (item) => item.id === 'configuration-panel' || (item.roles && item.roles.includes(ROLES.CONFIG_ADMIN))
        );
      } else {
        this._cachedNavigation = filteredByTrackableAdmin.filter((item) => {
          if (!item.roles) return true;

          if (Array.isArray(item.roles) && userRole !== null && item.roles.includes(userRole)) {
            return true;
          }

          if (userRole === ROLES.ADMIN && canTrackTime && Array.isArray(item.roles)) {
            return item.roles.includes(ROLES.USER) || item.roles.includes(ROLES.MANAGER);
          }

          return false;
        });
      }

      this._navigation.next({
        compact: this._cachedNavigation,
      });
    }

    return this._navigation.asObservable();
  }

  /**
   * Update the navigation
   *
   * @param navigation
   */
  update(navigation: FuseNavigationItem[]): void {
    // Update the navigation
    this._navigation.next({
      compact: navigation,
    });
  }
}
